// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useContext, useRef, useReducer, useEffect } from "react"
import parseDuration from "parse-duration"

export const makeContextSelector = (context) => {
  return (
    selector,
    isSame = (a, b) => JSON.stringify(a) === JSON.stringify(b),
  ) => {
    const full = useContext(context)
    const selected = selector(full)
    // store last selected in ref
    const ref = useRef(selected)
    const [, force] = useReducer((c) => c + 1, 0)

    useEffect(() => {
      if (!isSame(ref.current, selected)) {
        ref.current = selected
        force()
      }
    }, [isSame, selected])

    return ref.current
  }
}

export const dateDiffText = (date) => {
  const diffRaw = new Date(date).getTime() - new Date().getTime()
  const isFuture = diffRaw > 0
  const diff = Math.abs(diffRaw)
  if (diff < 1000 * 60) {
    return "just now"
  }

  const diffMinutes = Math.floor(diff / (1000 * 60))
  const diffHours = Math.floor(diff / (1000 * 60 * 60))
  const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24))
  const diffWeeks = Math.floor(diff / (1000 * 60 * 60 * 24 * 7))

  const remainingDays = diffDays % 7
  const remainingHours = diffHours % 24
  const remainingMinutes = diffMinutes % 60

  let result = ""
  if (diffWeeks > 0) {
    result = `${diffWeeks}w${remainingDays > 0 ? remainingDays + "d" : ""}`
  } else if (diffDays > 0) {
    result = `${diffDays}d${remainingHours > 0 ? remainingHours + "h" : ""}`
  } else if (diffHours > 0) {
    result = `${diffHours}h${
      remainingMinutes > 0 ? remainingMinutes + "m" : ""
    }`
  } else {
    result = `${diffMinutes}m`
  }

  return isFuture ? "in " + result.trim() : result.trim() + " ago"
}

export const formatCurrency = (amount: number, currency: string = "USD") => {
  try {
    // Use the browser's default locale
    return amount.toLocaleString("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    })
  } catch (e) {
    // fallback: just return amount and currency code
    return `${amount} ${currency}`
  }
}

export const formatDate = (date: string) => {
  return new Date(date).toLocaleString("fr-FR")
}

// debt: dead export kept alive only to retain the `parse-duration` dependency;
// upgrade trigger: remove this export together with `parse-duration` from
// package.json in a lockfile-touching follow-up (out of this worker's zone).
export const durationToSeconds = (text: string) => {
  return parseDuration(text)
}
